package com.devi3east.bashbuddy.search.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.features.common.util.DateTimeFormatUtil
import com.devi3east.bashbuddy.features.common.util.ModernCalendarPicker
import com.devi3east.bashbuddy.search.ui.SearchFilterType
import com.devi3east.bashbuddy.search.ui.SearchFilters

@Composable
fun FilterBar(
    filters: SearchFilters,
    availableStates: List<String>,
    onFiltersChanged: (SearchFilters) -> Unit,
    onClearFilters: () -> Unit
) {
    var activeSheet by remember { mutableStateOf<SearchFilterType?>(null) }
    var showDatePicker by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 8.dp),
        contentAlignment = Alignment.Center
    ) {

        Column(
            modifier = Modifier
                .widthIn(max = 700.dp)
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
        ) {

            // ---------- TOP ROW ----------
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally)
            ) {

                FilterChip(
                    selected = false,
                    onClick = onClearFilters,
                    label = { Text("Clear") },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer,
                        labelColor = MaterialTheme.colorScheme.onErrorContainer
                    )
                )

                FilterChip(
                    selected = filters.filterType == SearchFilterType.EVENT_TITLE,
                    onClick = {
                        onFiltersChanged(
                            filters.copy(filterType = SearchFilterType.EVENT_TITLE)
                        )
                    },
                    label = { Text("Event") },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        labelColor = MaterialTheme.colorScheme.onSurface,
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )

                FilterChip(
                    selected = filters.filterType == SearchFilterType.ORGANIZATION,
                    onClick = {
                        onFiltersChanged(
                            filters.copy(filterType = SearchFilterType.ORGANIZATION)
                        )
                    },
                    label = { Text("Organization") },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        labelColor = MaterialTheme.colorScheme.onSurface,
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )
            }

            // ---------- SECOND ROW ----------
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally)
            ) {

                FilterChip(
                    selected = filters.state != null,
                    onClick = { activeSheet = SearchFilterType.STATE },
                    label = { Text(getChipLabel(SearchFilterType.STATE, filters)) },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        labelColor = MaterialTheme.colorScheme.onSurface,
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )

                FilterChip(
                    selected = filters.month != null,
                    onClick = { activeSheet = SearchFilterType.MONTH },
                    label = { Text(getChipLabel(SearchFilterType.MONTH, filters)) },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        labelColor = MaterialTheme.colorScheme.onSurface,
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )

                FilterChip(
                    selected = filters.date != null,
                    onClick = { showDatePicker = true },
                    label = { Text(getChipLabel(SearchFilterType.DATE, filters)) },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        labelColor = MaterialTheme.colorScheme.onSurface,
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )

                /*FilterChip(
                    selected = filters.distance != null,
                    onClick = { activeSheet = SearchFilterType.DISTANCE },
                    label = { Text(getChipLabel(SearchFilterType.DISTANCE, filters)) },
                    colors = FilterChipDefaults.filterChipColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        labelColor = MaterialTheme.colorScheme.onSurface,
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                )*/
            }
        }

        // ---------- SHEETS ----------
        when (activeSheet) {

            SearchFilterType.STATE -> {
                StateFilterSheet(
                    states = availableStates,
                    onApply = {
                        onFiltersChanged(filters.copy(state = it))
                        activeSheet = null
                    },
                    onDismiss = { activeSheet = null }
                )
            }

            SearchFilterType.MONTH -> {
                MonthFilterSheet(
                    onApply = {
                        onFiltersChanged(filters.copy(month = it))
                        activeSheet = null
                    },
                    onDismiss = { activeSheet = null }
                )
            }

            SearchFilterType.DISTANCE -> {
                DistanceFilterSheet(
                    onApply = {
                        onFiltersChanged(filters.copy(distance = it))
                        activeSheet = null
                    },
                    onDismiss = { activeSheet = null }
                )
            }

            else -> {}
        }
    }

    // ---------- DATE PICKER ----------
    if (showDatePicker) {
        ModernCalendarPicker(
            initialInstant = filters.date,
            onDateSelected = { instant ->
                onFiltersChanged(filters.copy(date = instant))
                showDatePicker = false
            },
            onDismiss = { showDatePicker = false }
        )
    }
}

private fun getChipLabel(type: SearchFilterType, filters: SearchFilters): String {
    return when (type) {
        SearchFilterType.EVENT_TITLE -> "Event"
        SearchFilterType.ORGANIZATION -> filters.organization ?: "Organization"
        SearchFilterType.STATE -> filters.state ?: "State"
        SearchFilterType.MONTH -> monthName(filters.month)
        SearchFilterType.DATE -> filters.date?.let { DateTimeFormatUtil.instantToDateString(it) } ?: "Date"
        SearchFilterType.DISTANCE -> filters.distance?.let { "$it miles" } ?: "Distance"
    }
}

private fun monthName(month: Int?): String {
    return when (month) {
        1 -> "January"
        2 -> "February"
        3 -> "March"
        4 -> "April"
        5 -> "May"
        6 -> "June"
        7 -> "July"
        8 -> "August"
        9 -> "September"
        10 -> "October"
        11 -> "November"
        12 -> "December"
        else -> "Month"
    }
}